<template>
  <div
    v-if="isGroup"
    data-slot="condition-definition-group"
    :class="[ 'flex flex-col gap-2', level > 1 && 'rounded-xl border border-border bg-background/40 p-3' ]"
  >
    <!-- eslint-disable-next-line vue/no-v-for-template-key -->
    <template v-for="(child, index) in node.items" :key="'node-' + index">
      <condition-definition
        :item="item"
        :tree-node="child"
        :ad-account="adAccount"
        :side="side"
        :level="level + 1"
      />

      <!-- A flipped AND/OR is spelled out on the new side ("OR → AND"). -->
      <div v-if="index < node.items.length - 1" :key="'connector-' + index" class="flex items-center gap-2.5">
        <span :class="[ 'h-px flex-1', logicChange ? 'bg-warning' : 'bg-border' ]" />
        <span v-if="logicChange && side === 'new'" class="rounded-full bg-warning px-3 py-0.5 text-xs font-semibold tracking-wide text-warning-foreground">
          {{ tr(logicChange.from) }} → {{ tr(logicChange.to) }}
        </span>
        <span
          v-else
          :class="[
            'rounded-full px-3 py-0.5 text-xs font-semibold tracking-wide',
            logicChange ? 'bg-warning text-warning-foreground' : logicClass === 'and' ? 'bg-primary text-white' : 'bg-primary/45 text-white',
          ]"
        >
          {{ tr(node.logic) }}
        </span>
        <span :class="[ 'h-px flex-1', logicChange ? 'bg-warning' : 'bg-border' ]" />
      </div>
    </template>
  </div>

  <div
    v-else
    data-slot="condition-definition-row"
    :class="[ 'relative flex flex-col gap-1 rounded-lg border px-3.5 py-2.5 text-sm', statusClass ]"
  >
    <span v-if="badgeLabel" :class="[ 'absolute top-2 right-2.5 rounded-full bg-card px-2 py-px text-xs font-semibold', badgeClass ]">{{ badgeLabel }}</span>

    <div :class="[ 'flex flex-wrap items-center gap-1.5', badgeLabel && 'pr-20', isRemoved && 'line-through decoration-destructive' ]">
      <span class="font-semibold text-foreground">{{ fieldLabel }}</span>
      <span v-if="datePeriodLabel" class="text-xs text-muted-foreground">{{ datePeriodLabel }}</span>
      <span v-if="subfieldLabel" class="text-xs text-muted-foreground">· {{ subfieldLabel }}</span>
    </div>

    <div :class="[ '[overflow-wrap:anywhere]', isRemoved && 'line-through decoration-destructive' ]">
      <template v-if="isTextField">
        <span class="mr-1 text-muted-foreground">{{ comparisonLabel }}</span>
        <span class="font-semibold text-foreground">{{ searchTerm }}</span>
      </template>
      <span v-else class="font-semibold text-foreground">{{ requirementText }}</span>
    </div>
  </div>
</template>

<script>
import AdAccount from '@/entities/AdAccount';
import RuleChange, { LEAF_STATUS } from '@/entities/RuleChange.js';
import ConditionLeafMixin from '@/mixins/ConditionLeaf.js';

export default {
  name: 'ConditionDefinition',
  mixins: [ ConditionLeafMixin ],
  props: {
    item: {
      type: RuleChange,
      required: true,
    },
    treeNode: {
      type: Object,
      default: null,
    },
    side: {
      type: String,
      default: 'new',
    },
    adAccount: {
      type: [ AdAccount, null ],
      default: null,
    },
    level: {
      type: Number,
      default: 1,
    },
  },
  computed: {
    node() {
      return this.treeNode || (this.side === 'original' ? this.item.original : this.item.new);
    },
    statusMap() {
      return this.item.conditionDiff;
    },
    fields() {
      return this.$store.getters['app/fields'];
    },
    comparisons() {
      return this.$store.getters['app/fieldComparisons'];
    },
    rawStatus() {
      return this.statusMap.get(this.node) ?? null;
    },
    logicChange() {
      return this.rawStatus && typeof this.rawStatus === 'object' ? this.rawStatus : null;
    },
    status() {
      return typeof this.rawStatus === 'string' ? this.rawStatus : null;
    },
    statusClass() {
      switch (this.status) {
        case LEAF_STATUS.ADDED:
          return 'border-success/60 bg-success/15';
        case LEAF_STATUS.REMOVED:
          return 'border-destructive/50 bg-destructive/10';
        case LEAF_STATUS.MODIFIED:
          return 'border-warning/60 bg-warning/15';
        default:
          return 'glass-tile border-border';
      }
    },
    isRemoved() {
      return this.status === LEAF_STATUS.REMOVED;
    },
    badgeClass() {
      switch (this.status) {
        case LEAF_STATUS.ADDED:
          return 'text-success';
        case LEAF_STATUS.REMOVED:
          return 'text-destructive';
        default:
          return 'text-warning';
      }
    },
    badgeLabel() {
      switch (this.status) {
        case LEAF_STATUS.ADDED:
          return this.tr('Added');
        case LEAF_STATUS.REMOVED:
          return this.tr('Removed');
        case LEAF_STATUS.MODIFIED:
          return this.tr('Modified');
        default:
          return null;
      }
    },
  },
};
</script>
